<template>
	<nk-page :commercialAccess="['Sale', 'Warehouse']" :policyClaims="['Product_ProductClassification_Management']">
		<template #badges>
			<nk-badge system="sale">
				<nk-badge-item type="system" modelValue="sale"></nk-badge-item>
			</nk-badge>
		</template>
		<ag-grid :options="componentOptions.table" :data="productClassificationList" :key="rerenderGrid" v-model="selectedRow" @row_dbClicked="rowDbClicked" @preSelectedRowData="preSelectedRowData" ref="productClassificationTable" tabindex="100" startup></ag-grid>

	</nk-page>
</template>

<script>
	import Endpoints from '@nikan/endpoints'
	import { mapActions, mapGetters } from 'vuex'
	import { post } from '~/plugins/nikan-api'
	import BooleanView from '~/components/form/boolean-label'
	import $sc from '~/plugins/shortcut.collection.js'
	import productClassificationModify from './product-classification-modify.vue'
	import dialog from '~/plugins/nikan-dialog-plugin'
	import emptyId from '../../../statics/empty-id'
	import _ from 'lodash'

	export default {
		name: 'product-classification-list',
		modal: {
			size: 'md',
			stateUID: 'product-classification',
			helpTag: 'ProductClassificationList'
		},
		props: {
			data: { type: Array },
			options: { type: Object }
		},
		mounted() {
			this.settings = _.merge(this.settings, this.options)
			this.settings.modelValue && (this.selectedRow = this.settings.modelValue)

			if (!this.data || !this.data.length)
				this.getProductClassificationList()

			let buttonsArray = [this.pageButtons.newButton, this.pageButtons.editButton, this.pageButtons.deleteButton]
			if (this.grandParentType() === 'modal')
				buttonsArray.splice(0, 0, this.pageButtons.selectButton)

			this.prepareWindow(this.$t('sale.product-classification.title'), buttonsArray)
		},
		data() {
			return {
				productClassificationList: [],
				rerenderGrid: 0,
				pageButtons: {
					newButton: {
						title: this.$t('sale.product-classification.new'),
						shortcut: $sc.find('add'),
						icon: 'plus',
						isPrimary: false,
						handle: () => {
							this.newProductClassification()
						},
						policyClaims: 'Product_ProductClassification_Add',
					},
					editButton: {
						title: this.$t('edit'),
						shortcut: $sc.find('edit'),
						visible: true,
						icon: 'edit',
						handle: () => {
							this.editProductClassification()
						},
						policyClaims: 'Product_ProductClassification_Edit',
					},
					deleteButton: {
						title: this.$t('delete'),
						shortcut: $sc.find('delete'),
						visible: true,
						icon: 'trash',
						handle: () => {
							this.showDeleteDialog()
						},
						policyClaims: 'Product_ProductClassification_Delete',
					},
					selectButton: {
						title: this.$t('select'),
						shortcut: $sc.find('enter-only'),
						element: () => this.$refs.productClassificationTable.$el,
						icon: 'check',
						isPrimary: true,
						visible: true,
						handle: (button, page, e) => {
							this.resolve(this.selectedRow)
						}
					},
				},
				settings: {},
				selectedRow: null,
				componentOptions: {
					table: {
						id: this.$options.name,
						focusAndSelect: {
							allowFocus: true
						},
						doubleClickedToSelect: this.grandParentType() === 'modal',
						rowGroupPanelShow: false,
						groupIncludeTotalFooter: false,
						cellSelectionAggReport: false,
						enterToSelect: true,
						pagination: false,
						fitToWidth: false,
						sideBar: 'none',
						rowSelection: {
							mode: 'singleRow'
						},
						columnDefs: [
							{
								field: 'rowNumber',
								headerName: this.$t('row-number'),
								pinned: 'right',
								width: 60,
							},
							{
								field: 'code',
								headerName: this.$t('sale.product-classification.code'),
								width: 100,
							},
							{
								field: 'name',
								headerName: this.$t('sale.product-classification.f.name'),
								width: 100,
							},
							{
								field: 'pricingType',
								headerName: this.$t('sale.product-classification.f.pricingType'),
								contentType: 'enum',
								valueFormatter: data => `${this.$t(`PricingTypes.${data.value.toLowerCase()}`)}`,
								width: 100,
							},
							{
								field: 'standardizable',
								headerName: this.$t('sale.product-classification.f.standard'),
								width: 120,
								contentType: 'enum',
								valueFormatter: data => data.value ? 'بله' : 'خیر',
								cellRenderer: BooleanView,
								cellRendererParams: (data) => {
									return {
										options: {
											useColors: true,
											modelValue: data.value
										}
									}
								},
							},

							{
								headerName: this.$t('sale.product-classification.f.inventoryAccount'),
								children: [
									{
										field: 'inventoryAccount',
										headerName: this.$t('sale.product-classification.f.account'),
										contentType: 'enum',
										valueFormatter: data => data.value?.name,
										width: 150,
									},
									{
										field: 'inventoryAccountCenter',
										headerName: this.$t('sale.product-classification.f.center'),
										contentType: 'enum',
										valueFormatter: data => data.value ? `${data.value.name} (${data.value.accountingCode})` : '',
										width: 150,
									},
								]
							},

							{
								headerName: this.$t('sale.product-classification.f.taxAccount'),
								children: [
									{
										field: 'taxAccount',
										headerName: this.$t('sale.product-classification.f.account'),
										width: 150,
										contentType: 'enum',
										valueFormatter: data => data.value?.name,
									},
									{
										field: 'taxAccountCenter',
										headerName: this.$t('sale.product-classification.f.center'),
										width: 150,
										contentType: 'enum',
										valueFormatter: data => data.value ? `${data.value.name} (${data.value.accountingCode})` : '',
									},
								]
							},

							{
								headerName: this.$t('sale.product-classification.f.transportCostAccount'),
								children: [
									{
										field: 'transportCostAccount',
										headerName: this.$t('sale.product-classification.f.account'),
										width: 150,
										contentType: 'enum',
										valueFormatter: data => data.value?.name,
									},
									{
										field: 'transportCostAccountCenter',
										headerName: this.$t('sale.product-classification.f.center'),
										width: 150,
										contentType: 'enum',
										valueFormatter: data => data.value ? `${data.value.name} (${data.value.accountingCode})` : '',
									},
								]
							},

							{
								headerName: this.$t('sale.product-classification.f.buySuspendedAccount'),
								children: [
									{
										field: 'buySuspendedAccount',
										headerName: this.$t('sale.product-classification.f.account'),
										width: 150,
										contentType: 'enum',
										valueFormatter: data => data.value?.name,
									},
									{
										field: 'buySuspendedAccountCenter',
										headerName: this.$t('sale.product-classification.f.center'),
										width: 150,
										contentType: 'enum',
										valueFormatter: data => data.value ? `${data.value.name} (${data.value.accountingCode})` : '',
									},
								]
							},

							{
								headerName: this.$t('sale.product-classification.f.discountAccount'),
								children: [
									{
										field: 'discountAccount',
										headerName: this.$t('sale.product-classification.f.account'),
										width: 150,
										contentType: 'enum',
										valueFormatter: data => data.value?.name,
									},
									{
										field: 'discountAccountCenter',
										headerName: this.$t('sale.product-classification.f.center'),
										width: 150,
										contentType: 'enum',
										valueFormatter: data => data.value ? `${data.value.name} (${data.value.accountingCode})` : '',
									},
								]
							},

							{
								headerName: this.$t('sale.product-classification.f.saleAccount'),
								children: [
									{
										field: 'saleAccount',
										headerName: this.$t('sale.product-classification.f.account'),
										width: 150,
										contentType: 'enum',
										valueFormatter: data => data.value?.name,
									},
									{
										field: 'saleAccountCenter',
										headerName: this.$t('sale.product-classification.f.center'),
										width: 150,
										contentType: 'enum',
										valueFormatter: data => data.value ? `${data.value.name} (${data.value.accountingCode})` : '',
									},
								]
							},

							{
								headerName: this.$t('sale.product-classification.f.costOfSoldProductAccount'),
								children: [
									{
										field: 'costOfSoldProductAccount',
										headerName: this.$t('sale.product-classification.f.account'),
										width: 150,
										contentType: 'enum',
										valueFormatter: data => data.value?.name,
									},
									{
										field: 'costOfSoldProductAccountCenter',
										headerName: this.$t('sale.product-classification.f.center'),
										width: 150,
										contentType: 'enum',
										valueFormatter: data => data.value ? `${data.value.name} (${data.value.accountingCode})` : '',
									},
								]
							},

							{
								headerName: this.$t('sale.product-classification.f.ourTrustWithOthersAccount'),
								children: [
									{
										field: 'ourTrustWithOthersAccount',
										headerName: this.$t('sale.product-classification.f.account'),
										width: 150,
										contentType: 'enum',
										valueFormatter: data => data.value?.name,
									},
									{
										field: 'ourTrustWithOthersAccountCenter',
										headerName: this.$t('sale.product-classification.f.center'),
										width: 150,
										contentType: 'enum',
										valueFormatter: data => data.value ? `${data.value.name} (${data.value.accountingCode})` : '',
									},
								]
							},

							{
								headerName: this.$t('sale.product-classification.f.othersTrustWithUsAccount'),
								children: [
									{
										field: 'othersTrustWithUsAccount',
										headerName: this.$t('sale.product-classification.f.account'),
										width: 150,
										contentType: 'enum',
										valueFormatter: data => data.value?.name,
									},
									{
										field: 'othersTrustWithUsAccountCenter',
										headerName: this.$t('sale.product-classification.f.center'),
										width: 150,
										contentType: 'enum',
										valueFormatter: data => data.value ? `${data.value.name} (${data.value.accountingCode})` : '',
									},
								]
							},

						],

					},
				},
			}
		},
		computed: {
			...mapGetters({
				all: 'productClassifications/all'
			}),
			// productClassificationList() {
			//   if (this.data && this.data.length)
			//     return this.data
			//   return this.all
			// },
		},
		watch: {
			'$store.state.productClassifications.items': {
				deep: true,
				immediate: true,
				handler(val) {
					this.productClassificationList = val
					this.rerenderGrid++
				}
			}
		},
		methods: {
			...mapActions({
				broadcastRefresh: 'socket/broadcastRefresh',
				refresh_productClassifications: 'productClassifications/refresh',
			}),
			preSelectedRowData(data) {
				this.selectedRow = data
			},
			rowDbClicked(val) {
				if (this.grandParentType() === 'modal')
					this.resolve(val)
			},
			async getProductClassificationList(force = false) {
				var wait = this.$wait(this.$refs.productClassificationTable.$el)
				this.refresh_productClassifications({ force: force })
					.then(x => {
						wait.end()
						// this.selectedRow = _.head(this.productClassificationList)
					}).catch(err => {
						wait.end()
					})
			},
			newProductClassification() {
				this.showModal({
					component: productClassificationModify,
				})
					.then(res => this.broadcastRefresh('ProductClassifications'))
					.catch(() => { })
			},
			showDeleteDialog() {
				if (!this.selectedRow || !this.selectedRow.id || this.selectedRow.id == emptyId)
					return this.$toast(this.$t('document.m.select-row'))

				dialog({
					title: this.$t('sale.product-classification.m.delete-title'),
					body: this.$t('sale.product-classification.m.delete-body', {
						name: this.selectedRow.name,
					}),
					type: 'warning',
					autoClose: true,
					buttons: [
						{
							title: this.$t('dialog.yes'),
							shortcut: $sc.find('yes', 'dialog'),
							isPrimary: true,
							handle: (button, page, e) => {
								this.deleteProductClassification()
							},
						},
						{
							title: this.$t('dialog.cancel'),
							shortcut: $sc.find('cancel', 'dialog'),
							isCancel: true,
							handle: (button, page, e) => { },
						},
					],
				})
			},
			editProductClassification() {
				if (!this.selectedRow)
					return this.$toast(this.$t('document.m.select-row'))
				this.showModal({
					component: productClassificationModify,
					data: this.selectedRow,
				})
					.then(res => this.broadcastRefresh('ProductClassifications'))
					.catch(() => { })
			},
			deleteProductClassification() {
				post(Endpoints.getUrl('productClassification.delete'), { productClassificationId: this.selectedRow.id })
					.then(res => {
						this.$toast(this.$t('sale.product-classification.m.delete-success'), { type: 'success' })
						this.broadcastRefresh('ProductClassifications')
					})
			},
		}
	}
</script>

<style></style>
